import { Link } from "@tanstack/react-router";
import * as React from "react";
import { useState } from "react";
import { ui } from "@patient-watch/ui";
import { NavigationMenuLink, NavigationMenuList } from "@patient-watch/ui/navigation-menu";
import { useUserLabelAccessQuery } from "@/features/auth/api/selectUserLabelAccess";
import { useLogoutMutation } from "@/features/auth/api/useLogoutMutation";
import { useProfileQuery } from "@/features/profile/api/selectProfile";
import { cn } from "@/lib/utils";
import type { ExtendedProfileT } from "@patient-watch/types";
import { useAuth } from "@/utils/auth";
import { buttonVariants } from "@patient-watch/ui/button";
import { Separator } from "@patient-watch/ui/separator";
import useNavigationContent from "../hooks/useNavigationContent";
import { filterAppNavigation } from "../services/filterAppNavigation";
import NotificationCount from "./NotificationCount";
import ProfileNotificationCount from "./ProfileNotificationCount";
import ValidationNotificationCount from "./ValidationNotificationCount";
import WorklistNotificationCount from "./WorklistNotificationCount";
import type { PageT } from "./navigation-content";
import {
    checkIfSelected as checkIfSelectedUtil,
    getSharedNavigationStyles,
    showNotificationCount,
    showProfileNotificationCount,
    showValidationNotificationCount,
    showWorklistNotificationCount,
} from "./navigation-utils";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { getWebsiteHelpCentreAnchorProps } from "@/features/help/utils/websiteHelpCentre";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@patient-watch/ui/dropdown-menu";
import { ChevronDown, CircleHelp, LogOut, UserRound, UsersRound } from "lucide-react";

function getButtonClassNames(page: PageT) {
    const selected = checkIfSelectedUtil(page.href as string);

    return cn(
        buttonVariants({ variant: page.variant ?? "ghost", size: "sm" }),
        page.variant === "ghost" && "bg-transparent backdrop-blur-none ring-0",
        !page.variant && "border-0 bg-transparent",
        page.variant !== "ghost" && "ml-2",
        getSharedNavigationStyles(selected, false)
    );
}

function getTextClassNames(page: PageT, numberNavigationItems: number) {
    return cn(page.iconOnly && "sr-only", numberNavigationItems > 6 && "sr-only");
}

interface NavigationItemContentProps {
    page: PageT;
    numberNavigationItems: number;
}

function NavigationItemContent({ page, numberNavigationItems }: NavigationItemContentProps) {
    const [isHovered, setIsHovered] = useState(false);

    // Standard icon props for consistency across all navigation icons
    const standardIconProps = {
        size: 30,
        strokeWidth: 1.5,
        color: "currentColor", // Lucide follows this. Lottie paints a light stroke when the app is dark.
        isHovered,
    };

    // Clone the icon element and pass standard props
    const iconElement = React.isValidElement(page.icon)
        ? React.cloneElement(page.icon as React.ReactElement<any>, {
              ...standardIconProps,
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
          })
        : page.icon;

    return (
        <span
            className="flex items-center gap-x-[6px]"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {iconElement}
            <span className={getTextClassNames(page, numberNavigationItems)}>{page.name}</span>
            {showNotificationCount(page.href) && (
                <span className="ml-0.5">
                    <NotificationCount />
                </span>
            )}
            {showWorklistNotificationCount(page.href) && (
                <span className="ml-0.5">
                    <WorklistNotificationCount />
                </span>
            )}
            {showValidationNotificationCount(page.href) && (
                <span className="ml-0.5">
                    <ValidationNotificationCount />
                </span>
            )}
            {showProfileNotificationCount(page.href) && (
                <span className="ml-0.5">
                    <ProfileNotificationCount />
                </span>
            )}
        </span>
    );
}

interface AccountMenuProps {
    accountPages: PageT[];
    displayName: string;
    helpLabel: string;
    helpProps: ReturnType<typeof getWebsiteHelpCentreAnchorProps>;
    logoutLabel: string;
    onLogout: () => void;
}

function AccountMenu({ accountPages, displayName, helpLabel, helpProps, logoutLabel, onLogout }: AccountMenuProps) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <ui.Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 px-2.5 text-sm"
                    data-testid="account-menu-button"
                >
                    <UserRound className="w-[18px] h-[18px]" />
                    <span className="max-w-32 truncate">{displayName}</span>
                    <ProfileNotificationCount />
                    <ChevronDown className="opacity-60 w-4 h-4" />
                </ui.Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-sm">{displayName}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {accountPages.map((page) => (
                    <DropdownMenuItem key={page.href} asChild className="gap-2 text-sm cursor-pointer">
                        <Link to={page.href} data-testid={`nav-${page.name.toLowerCase().replace(/\s+/g, "-")}`}>
                            {page.href === "/team" ? (
                                <UsersRound className="w-[18px] h-[18px]" />
                            ) : (
                                <UserRound className="w-[18px] h-[18px]" />
                            )}
                            {page.name}
                            {showProfileNotificationCount(page.href) && (
                                <span className="ml-auto">
                                    <ProfileNotificationCount />
                                </span>
                            )}
                        </Link>
                    </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild className="gap-2 text-sm cursor-pointer">
                    <a {...helpProps} data-testid="help-button">
                        <CircleHelp className="w-[18px] h-[18px]" />
                        {helpLabel}
                    </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    className="gap-2 text-sm cursor-pointer"
                    data-testid="logout-button"
                    onClick={onLogout}
                >
                    <LogOut className="w-[18px] h-[18px]" />
                    {logoutLabel}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function DesktopNavigation() {
    const auth = useAuth();
    const copy = useProfileCopy();
    const { locale } = useAppLocale();
    const { data: profile } = useProfileQuery();
    const { data: userLabels = [] } = useUserLabelAccessQuery(auth.id);
    const logout = useLogoutMutation();
    const navigationContent = useNavigationContent();
    const userLabelNames = userLabels.map((l) => l.label_name);
    const visibleNavigation = filterAppNavigation(
        navigationContent,
        auth.roles?.roles,
        profile as ExtendedProfileT,
        userLabelNames
    );
    const accountPages = visibleNavigation.filter((page) => page.href === "/profile" || page.href === "/team");
    const primaryNavigation = visibleNavigation.filter((page) => page.href !== "/profile" && page.href !== "/team");
    const numberNavigationItems = primaryNavigation.length;
    const displayName =
        [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() ||
        accountPages.find((page) => page.href === "/profile")?.name ||
        "Account";

    return (
        <>
            <NavigationMenuList className="">
                {primaryNavigation.map((page: PageT, index) => (
                    <React.Fragment key={page.href}>
                        {index > 0 && primaryNavigation[index - 1]?.group !== page.group && (
                            <Separator orientation="vertical" className="bg-slate-300 dark:bg-slate-600 mx-1 h-7" />
                        )}
                        <div>
                            <ui.TooltipProvider>
                                <ui.Tooltip>
                                    <ui.TooltipTrigger asChild>
                                        {page.onClick ? (
                                            <button
                                                type="button"
                                                className={getButtonClassNames(page)}
                                                onClick={page.onClick}
                                                data-testid={`nav-${page.name.toLowerCase().replace(/\s+/g, "-")}`}
                                            >
                                                <NavigationItemContent
                                                    page={page}
                                                    numberNavigationItems={numberNavigationItems}
                                                />
                                            </button>
                                        ) : (
                                            <Link
                                                to={page.href}
                                                className={getButtonClassNames(page)}
                                                data-testid={`nav-${page.name.toLowerCase().replace(/\s+/g, "-")}`}
                                            >
                                                <NavigationItemContent
                                                    page={page}
                                                    numberNavigationItems={numberNavigationItems}
                                                />
                                            </Link>
                                        )}
                                    </ui.TooltipTrigger>
                                    <ui.TooltipContent className="max-w-sm">
                                        <h3 className="text-slate-900 text-sm">{page.name}</h3>
                                        <p className="text-slate-600 text-sm">{page.description}</p>
                                    </ui.TooltipContent>
                                </ui.Tooltip>
                            </ui.TooltipProvider>
                        </div>
                    </React.Fragment>
                ))}
            </NavigationMenuList>
            <Separator orientation="vertical" className="bg-slate-300 dark:bg-slate-600 mx-2 h-10" />
            <AccountMenu
                accountPages={accountPages}
                displayName={displayName}
                helpLabel={copy.help}
                helpProps={getWebsiteHelpCentreAnchorProps(auth.roles, locale)}
                logoutLabel={copy.logout}
                onLogout={() => logout.mutate()}
            />
        </>
    );
}

export default DesktopNavigation;

const ListItem = React.forwardRef<React.ElementRef<"a">, React.ComponentPropsWithoutRef<"a">>(
    ({ className, title, children, ...props }, ref) => {
        return (
            <li>
                <NavigationMenuLink asChild>
                    <a
                        ref={ref}
                        className={cn(
                            "block space-y-1 hover:bg-accent focus:bg-accent p-3 rounded-md outline-none h-fit no-underline leading-none transition-colors hover:text-accent-foreground focus:text-accent-foreground select-none",
                            className
                        )}
                        {...props}
                    >
                        <div className="font-medium text-md line-clamp-2 leading-none">{title}</div>
                        <p className="text-muted-foreground text-sm line-clamp-2 leading-snug">{children}</p>
                    </a>
                </NavigationMenuLink>
            </li>
        );
    }
);
ListItem.displayName = "ListItem";
